import { apiClient, unwrap } from '@services/api';
import type {
  Advertisement,
  AdminOverview,
  Category,
  SystemSetting,
  SystemStatus,
  User,
  WhatsAppStatus,
} from '@/types';

export const adminService = {
  async getUsers(): Promise<User[]> {
    const res = await apiClient.get<User[]>('/admin/users');
    return unwrap(res);
  },

  async createUser(data: Partial<User> & { password: string }): Promise<User> {
    const res = await apiClient.post<User>('/admin/users', data);
    return unwrap(res);
  },

  async updateUser(id: string, data: Partial<User> & { password?: string }): Promise<User> {
    const res = await apiClient.patch<User>(`/admin/users/${id}`, data);
    return unwrap(res);
  },

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/admin/users/${id}`);
  },

  async addCredits(userId: string, credits: number): Promise<{ adCredits: number }> {
    const res = await apiClient.post<{ adCredits: number }>(`/admin/users/${userId}/credits`, {
      amount: credits,
    });
    return unwrap(res);
  },

  async getCategories(): Promise<Category[]> {
    const res = await apiClient.get<Category[]>('/admin/categories');
    return unwrap(res);
  },

  async createCategory(data: Partial<Category>): Promise<Category> {
    const res = await apiClient.post<Category>('/admin/categories', data);
    return unwrap(res);
  },

  async updateCategory(id: string, data: Partial<Category>): Promise<Category> {
    const res = await apiClient.patch<Category>(`/admin/categories/${id}`, data);
    return unwrap(res);
  },

  async deleteCategory(id: string): Promise<void> {
    await apiClient.delete(`/admin/categories/${id}`);
  },

  async getSettings(): Promise<SystemSetting[]> {
    const res = await apiClient.get<SystemSetting[]>('/admin/settings');
    return unwrap(res);
  },

  async updateSetting(key: string, value: string): Promise<SystemSetting> {
    const res = await apiClient.patch<SystemSetting>(`/admin/settings/${key}`, { value });
    return unwrap(res);
  },

  async getSystemStatus(): Promise<SystemStatus> {
    const res = await apiClient.get<SystemStatus>('/admin/system-status');
    return unwrap(res);
  },

  async getOverview(): Promise<AdminOverview> {
    const res = await apiClient.get<AdminOverview>('/admin/overview');
    return unwrap(res);
  },

  async getWhatsAppStatus(): Promise<WhatsAppStatus> {
    const res = await apiClient.get<WhatsAppStatus>('/notifications/whatsapp/status');
    return unwrap(res);
  },

  async sendTestWhatsApp(
    phone: string,
    message?: string,
  ): Promise<{ logId: string; status: string; errorMessage?: string }> {
    const res = await apiClient.post<{ logId: string; status: string; errorMessage?: string }>(
      '/notifications/test-wa',
      {
        phone,
        message,
      },
    );
    return unwrap(res);
  },

  async refreshWhatsAppQr(): Promise<{ refreshed: boolean; connected?: boolean; reason?: string }> {
    const res = await apiClient.post<{ refreshed: boolean; connected?: boolean; reason?: string }>(
      '/notifications/whatsapp/qr/refresh',
    );
    return unwrap(res);
  },

  async resetWhatsApp(): Promise<{ reset: boolean; reason?: string }> {
    const res = await apiClient.post<{ reset: boolean; reason?: string }>(
      '/notifications/whatsapp/reset',
    );
    return unwrap(res);
  },

  async getNotificationLogs(limit = 50): Promise<
    Array<{
      id: string;
      recipientPhone: string;
      message: string;
      channel: string;
      status: string;
      createdAt: string;
    }>
  > {
    const res = await apiClient.get<
      Array<{
        id: string;
        recipientPhone: string;
        message: string;
        channel: string;
        status: string;
        createdAt: string;
      }>
    >('/notifications/logs', { params: { limit } });
    return unwrap(res);
  },

  async getWhatsAppQr(): Promise<{ qrRequired: boolean; qrData: string | null }> {
    const res = await apiClient.get<{ qrRequired: boolean; qrData: string | null }>(
      '/notifications/whatsapp/qr',
    );
    return unwrap(res);
  },

  async getAllAds(status?: string): Promise<Advertisement[]> {
    const res = await apiClient.get<Advertisement[]>('/admin/ads', {
      params: status ? { status } : undefined,
    });
    return unwrap(res);
  },

  async createAd(data: Partial<Advertisement>): Promise<Advertisement> {
    const res = await apiClient.post<Advertisement>('/admin/ads', data);
    return unwrap(res);
  },

  async deleteAd(id: string): Promise<void> {
    await apiClient.delete(`/admin/ads/${id}`);
  },

  async toggleAd(id: string): Promise<{ isActive: boolean }> {
    const res = await apiClient.patch<{ isActive: boolean }>(`/admin/ads/${id}/toggle`);
    return unwrap(res);
  },

  async getPendingAds(): Promise<Advertisement[]> {
    const res = await apiClient.get<Advertisement[]>('/admin/ads', {
      params: { status: 'pending' },
    });
    return unwrap(res);
  },

  async approveAd(id: string): Promise<Advertisement> {
    const res = await apiClient.post<{ ad: Advertisement }>(`/admin/ads/${id}/approve`);
    return unwrap(res).ad;
  },

  async rejectAd(id: string, reason: string): Promise<Advertisement> {
    const res = await apiClient.post<{ ad: Advertisement }>(`/admin/ads/${id}/reject`, {
      reason,
    });
    return unwrap(res).ad;
  },

  async getAdRate(): Promise<{ creditsPerDay: number }> {
    const res = await apiClient.get<{ creditsPerDay: number }>('/admin/settings/ad-rate');
    return unwrap(res);
  },

  async setAdRate(creditsPerDay: number): Promise<{ creditsPerDay: number }> {
    const res = await apiClient.post<{ creditsPerDay: number }>('/admin/settings/ad-rate', {
      creditsPerDay,
    });
    return unwrap(res);
  },
};
